import { memo, useCallback, useEffect, useRef } from 'react'

import { MediaChannelEdit } from '@nativewaves/nw-cloud-sdk/media'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useMediaChannelUpdateMutation } from 'hooks/mutations/publishing'
import { mediaChannelConfigRoute } from 'pages/NWPlatform/MediaChannels/MediaChannel'
import { mediaChannelQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { environmentId, mediaChannelId } = mediaChannelConfigRoute.useParams()

  const mediaChannelQuery = useQuery({
    ...mediaChannelQueryKeys.detail({ channelId: mediaChannelId }),
    initialData: queryClient
      .getQueryData(mediaChannelQueryKeys.list({ environmentId }).queryKey)
      ?.pages.flatMap((page) => page.items)
      .find((item) => item.id === mediaChannelId),
  })
  const { data: mediaChannel } = mediaChannelQuery

  const { mutate: updateMediaChannel } = useMediaChannelUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: MediaChannelEdit) => {
      updateMediaChannel(
        { channelId: mediaChannelId, mediaChannelEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:MediaChannel.mediaChannelSaved'), {
              variant: 'success',
            })
          },
        },
      )
    },
    [enqueueSnackbar, mediaChannelId, t, updateMediaChannel],
  )

  useEffect(() => {
    if (editorInstanceRef.current && mediaChannelQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(mediaChannel, null, 4))
    }
  }, [mediaChannelQuery.isSuccess, mediaChannel])

  if (mediaChannelQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!mediaChannel) {
    return <NotFound />
  }

  return (
    <Editor
      value={mediaChannel}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
